import { ListItemButton, Paper, styled } from '@ethings/ui-toolkit';
import { Box } from '@mui/material';
import { SxProps } from '@mui/system';
import cx from 'classnames';
import { FC, PropsWithChildren, type JSX } from 'react';
import { useHref } from 'react-router-dom';
import { makeStyles } from '../../theme';

const useStyles = makeStyles()((theme) => ({
  item: {
    cursor: 'pointer',
    '&:hover': {
      boxShadow: theme.shadows[5],
    },
  },
  selected: {
    borderLeft: '4px solid',
    borderLeftColor: theme.palette.primary.main,
    borderRight: '4px solid',
    borderRightColor: theme.palette.primary.main,
  },
  deleted: {
    textDecoration: 'line-through',
  },
}));

interface Props extends PropsWithChildren {
  'data-testid'?: string;
  className?: string;
  elevation?: number;
  href?: string;
  selected?: boolean;
  sx?: SxProps;
  deleted?: boolean;
  disableAction?: boolean;
  disableRipple?: boolean;
  onClick?: () => void;
}

const StyledPaper = styled(Paper)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const StyledListItemButton = styled(ListItemButton)<{ clickable: boolean }>(({ clickable }) => ({
  cursor: clickable ? 'pointer' : 'default',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

export const ListPaper: FC<Props> = ({ children, className, elevation = 1, href, selected = false, sx, deleted = false, disableAction = false, disableRipple = false, onClick, ...props }): JSX.Element => {
  const { classes } = useStyles();
  const routedHref = useHref(href || '');
  const fullHref = href?.startsWith('#') ? href : href ? routedHref : undefined;

  const handleClick = (): void => {
    onClick?.();
    if (fullHref) {
      window.location.assign(fullHref);
    }
  };

  return (
    <StyledPaper
      data-testid={props['data-testid']}
      className={cx('ListPaper-root', { [classes.item]: !!href && !deleted, [classes.selected]: selected, [classes.deleted]: deleted }, className)}
      elevation={elevation}
    >
      <Box
        style={disableAction ? { padding: 'var(--ethings-spacing) calc(var(--ethings-spacing) * 2)' } : undefined}
        sx={sx}
      >
        {disableAction
          ? children
          : <StyledListItemButton
            data-testid="action-button"
            role={href ? 'link' : 'button'}
            onClick={handleClick}
            clickable={Boolean(href)}
            disableRipple={disableRipple}
          >
            {children}
          </StyledListItemButton>}
      </Box>
    </StyledPaper>
  );
};
